import React from "react";
import { useShallow } from "zustand/react/shallow";
import useStoreRoomScene from "store/useStoreRoomScene";
import Card from "./Card";
import { CARD_WIDTH, CARD_HEIGHT, CardProps } from "types/Card";
import { useRoomLayout } from "context/RoomLayoutContext";

const MAX_FAN_ANGLE = 30;
const MIN_CARD_VISIBLE_RATIO = 0.4;
const ROW_OVERLAP_RATIO = 0.9;
const DESKTOP_MAX_FAN_WIDTH = 530;
const toRadians = (deg: number) => (deg * Math.PI) / 180;

const computeFanRadius = (fanWidth: number, angleDeg: number): number => {
    const halfAngleRad = toRadians(angleDeg / 2);

    if(halfAngleRad <= 0) {
        return 800;
    }

    return (fanWidth - CARD_WIDTH) / (2 * Math.sin(halfAngleRad));
};

interface CardsContainerProps {
    className?: string;
    playerId: string;
    isHoverable: boolean;
}

type HandCardProps = Pick<CardProps, "id" | "type" | "canBePut" | "isHoverable"> & {
    transform: string;
};

const HandCard = React.memo(function HandCard({ transform, ...card }: HandCardProps) {
    const style = React.useMemo<React.CSSProperties>(() => ({
        transform,
        transformOrigin: "bottom center",
        transition: "transform 160ms ease-out",
        pointerEvents: "auto",
    }), [transform]);

    return <Card {...card} style={style} />;
});

const CardsContainer = ({ className, playerId, isHoverable }: CardsContainerProps) => {
    const cards = useStoreRoomScene(useShallow(state => state.cards[playerId] || []));
    const {
        orientation,
        scales,
        handFanMaxWidth,
    } = useRoomLayout();
    const [availableWidth, setAvailableWidth] = React.useState(0);

    React.useEffect(() => {
        const roomElement = document.getElementById("room") as HTMLDivElement;

        if(!roomElement) {
            return;
        }

        const updateWidth = () => {
            const width = roomElement.offsetWidth - CARD_WIDTH * 0.4 * 2;

            if(width > 0) {
                setAvailableWidth(width);
            }
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(roomElement);
        return () => observer.disconnect();
    }, []);

    if(availableWidth <= 0) {
        return null;
    }

    const handScale = scales.hand;
    const scaledWidth = CARD_WIDTH * handScale;
    const scaledHeight = CARD_HEIGHT * handScale;
    const fanWidth = orientation === "portrait"
        ? availableWidth
        : Math.min(availableWidth, handFanMaxWidth ?? DESKTOP_MAX_FAN_WIDTH);
    const fanRadius = computeFanRadius(fanWidth, MAX_FAN_ANGLE);
    const maxCardsPerRow = Math.max(2, Math.floor(fanWidth / (scaledWidth * MIN_CARD_VISIBLE_RATIO)));
    const angleStep = MAX_FAN_ANGLE / (maxCardsPerRow - 1);
    const rowHeight = scaledHeight + 30;
    const rowOverlap = scaledHeight * ROW_OVERLAP_RATIO;

    const rowCount = Math.ceil(cards.length / maxCardsPerRow);
    const rows = Array.from({ length: rowCount }, (_, i) =>
        cards.slice(i * maxCardsPerRow, (i + 1) * maxCardsPerRow)
    );

    return (
        <div className={`${className} flex flex-col items-center`}>
            {rows.map((row, rowIndex) => {
                const total = row.length;
                const rowAngle = total <= 1 ? 0 : angleStep * (total - 1);

                return (
                    <div key={`row-${rowIndex}`} className="relative" style={{
                        marginTop: rowIndex > 0 ? -rowOverlap : 0,
                        width: fanWidth,
                        height: rowHeight,
                        zIndex: rowIndex,
                        pointerEvents: "none",
                    }}>
                        {row.map((card, cardIndex) => {
                            const angleDeg = total <= 1 ? 0 : -rowAngle / 2 + cardIndex * angleStep;
                            const angleRad = toRadians(angleDeg);
                            const tx = fanRadius * Math.sin(angleRad);
                            const ty = fanRadius * (1 - Math.cos(angleRad));

                            return (
                                <div key={card.id} data-card-slot="" style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: 0,
                                    marginLeft: -(CARD_WIDTH / 2),
                                    zIndex: cardIndex,
                                    pointerEvents: "none",
                                }}>
                                    <HandCard
                                        id={card.id}
                                        type={card.type}
                                        canBePut={card.canBePut}
                                        isHoverable={isHoverable}
                                        transform={`translate(${tx}px, ${ty}px) rotate(${angleDeg}deg) scale(${handScale})`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export default React.memo(CardsContainer);
